/* eslint-disable @typescript-eslint/no-unused-vars */
// External libraries
import React, { useState } from 'react';
import { throttle } from 'throttle-debounce';
import { get, shuffle } from 'lodash';
import { observer, inject } from 'mobx-react';
import { useBexApi } from '@shared-ui/bex-api-context';
import { Viewport, ViewSmall, ViewMedium } from '@shared-ui/viewport-context';

// Uitk components
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkLayoutGrid } from 'uitk-react-layout-grid';
import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { useLodgingPwaPropertySearch } from '../../hooks/useLodgingPwaPropertySearch';
import {
  LodgingPwaPropertySearchQueryVariables,
  PaymentType,
  Points,
  TravelerType,
} from 'src/common/__generated__/api/types';

import {
  PropertySearchProps,
  DealsCarouselViewsProps,
  PropertySearchComponentProps,
  PropertySearchViewsProps,
} from './types';

import { DealsLoader } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-loader';
import { DealsSlickCarousel } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-carousel';
import { checkValue, mcicidFormatText, localization, ItemKeyHelper } from 'src/components/common/helper';
import gaLogger from 'src/utils/ga-logger';
import { isGridSlickView } from './deals-utils';
import { gridSlickView } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/views/grid-slick-view';
import {
  HotelCard,
  HotelCardProps,
} from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/hotel-card';
import { SeparatorProps } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-separator';
import { DealLoadingCards } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-loading-cards';
import { DealLoadingCardsProps } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/hotel-module-types';
import { getDeviceImage } from 'src/components/common/theme/component';
import { Sticker } from 'src/components/common/ui';
import { StickerProps } from 'src/components/common/module-util';

let initialPropertiesCount = 6;
let propertiesCount = initialPropertiesCount;
const maxPropertiesCount = 18;
let tracking = 'cp.cplite.sports';

const GetHotelSearchDeals = (propertySearchProps: PropertySearchProps) => {
  const { context } = useBexApi();

  const {
    regionName,
    regionId,
    priceMin,
    priceMax,
    sort,
    checkInDate,
    checkOutDate,
    adults,
    propertyCount,
    mcicidTemplateType,
    mcicidCampaignUrl,
    mcicidReferrerId,
    mcicidMainUrl,
    guestRating,
    starRating,
    travelerType,
    memberOnlyDeals,
    isRandom,
    cTAUrl,
    payWithPoints,
    paymentType,
  } = propertySearchProps;

  initialPropertiesCount = propertyCount;
  propertiesCount = propertyCount;

  const cmsStarRating = checkValue(starRating) ? parseInt(starRating?.replace(' stars', '0')) : 30;
  const starFilter = [];
  for (let i = cmsStarRating; i < 60; i += 10) {
    starFilter.push(Number(i));
  }
  const minGuestRating = checkValue(guestRating) ? `${guestRating}.0` : '4.0';
  const travelerTypes =
    checkValue(travelerType) && ['BUSINESS', 'FAMILY', 'LGBT', 'ROMANTIC'].includes(travelerType)
      ? [TravelerType[travelerType]]
      : null;
  const memberOnlyDealsFilter =
    checkValue(memberOnlyDeals) && memberOnlyDeals?.toLowerCase() === 'true' ? ['MEMBER_ONLY'] : null;

  const paymentTypes =
    checkValue(paymentType) &&
    [
      'COUPON_ELIGIBLE',
      'FREE_CANCELLATION',
      'GIFT_CARD',
      'PAY_LATER',
      'PAY_LATER_WITH_DEPOSIT',
      'PAY_WITHOUT_CREDITCARD',
    ].includes(paymentType)
      ? [PaymentType[paymentType]]
      : null;

  const lodgingPwaPropertySearchVariables: LodgingPwaPropertySearchQueryVariables = {
    context,
    destination: {
      regionName: regionName,
      regionId: regionId,
      pinnedPropertyId: null,
      propertyIds: null,
      mapBounds: null,
    },
    filters: {
      starList: starFilter,
      structureTypes: null,
      propertyName: null,
      neighborhood: regionId,
      mealPlan: null,
      amenities: null,
      accessibility: null,
      travelerType: travelerTypes,
      reviewScore: {
        min: Number(`${minGuestRating}`),
        max: Number('5.0'),
      },
      poi: null,
      paymentType: paymentTypes,
      cleaningAndSafetyPractices: null,
      deals: null,
      propertyStyles: null,
      rewards: memberOnlyDealsFilter,
      bedroomFilter: [],
      price: {
        min: Number(priceMin),
        max: Number(priceMax),
      },
      commissionTiers: null,
      agencyBusinessModels: null,
      chain: null,
      stayTypes: null,
    },
    legacyCriteria: {
      sort: sort,
      stars: starFilter.join(','),
      priceBuckets: [Number(priceMin), Number(priceMax)],
      amenities: [],
      structureTypes: [],
      guestRating: `${minGuestRating}, 5.0`,
      paymentType: [],
    },
    rooms: [
      {
        adults: Number(adults),
        children: [],
      },
    ],
    propertyShopOptions: {
      points:
        checkValue(payWithPoints) && payWithPoints?.toLowerCase() === 'true'
          ? Points.SHOP_WITH_POINTS
          : Points.SHOP_WITHOUT_POINTS,
    },
    shoppingContext: {
      multiItem: null,
    },
    sort: sort,
    dateRange: {
      checkInDate: checkInDate,
      checkOutDate: checkOutDate,
    },
    searchPagination: { size: 24 },
    searchIntent: {
      semdtl: null,
      theme: null,
      userIntent: null,
    },
    shouldFetchSortAndFilter: true,
    returnPropertyType: true,
  };

  const { data, error, loading } = useLodgingPwaPropertySearch(lodgingPwaPropertySearchVariables);

  return { data, error, loading };
};

const HotelSearchComponent: React.FC<PropertySearchProps> = (propertySearchProps: PropertySearchProps) => {
  const {
    cTACopy,
    propertyCount,
    mcicidTemplateType,
    mcicidCampaignUrl,
    mcicidReferrerId,
    mcicidMainUrl,
    extraParam = '',
    visibleControl,
    stickerPosition,
    stickerImage,
    isPagination,
    travelerType,
    parentId = '',
    memberOnlyDeals,
    isRandom,
    cTAUrl,
    payWithPoints,
    paymentType,
    hideOfferSummary,
  } = propertySearchProps;

  tracking = mcicidFormatText(mcicidTemplateType, mcicidCampaignUrl, mcicidMainUrl, mcicidReferrerId, extraParam);
  initialPropertiesCount = propertyCount;
  propertiesCount = propertyCount;

  const { data, error, loading } = GetHotelSearchDeals(propertySearchProps);
  let isLoadingHtml = <></>;
  let dealPropertyHtml = <></>;
  const getVisibleControl = isGridSlickView(visibleControl);
  if (loading) {
    const dealLoadingCardsProps: DealLoadingCardsProps = {
      view: { view: 'grid' },
      desktopLoadingCards: checkValue(propertyCount) ? propertyCount : 3,
    };

    isLoadingHtml = <>{DealLoadingCards(dealLoadingCardsProps)}</>;
  } else {
    dealPropertyHtml = (
      <>
        <PropertySearchComponent
          data={data}
          errorMessage={error?.message}
          viewMoreCtaText={cTACopy}
          visibleControl={visibleControl}
          isPagination={isPagination}
          isRandom={isRandom}
          cTAUrl={cTAUrl}
          hideOfferSummary={hideOfferSummary}
        />
      </>
    );
  }

  //hide the module
  if (!loading && !checkValue(get(data, 'propertySearch.propertySearchListings', []), true)) {
    const elm = document.getElementById(parentId);
    if (elm) {
      elm.style.display = 'none';
    }
  }

  if (
    !loading &&
    !checkValue(data) &&
    !checkValue(data?.propertySearch) &&
    !checkValue(data?.propertySearch?.propertySearchListings, true)
  ) {
    return <></>;
  }

  const stickerProps: StickerProps = {
    stickerImage,
    stickerPosition,
    exClass: 'hotel-sticker-container',
  };

  return (
    <div className={`hotel-tab-container ${!checkValue(cTAUrl) ? 'bottom-extra-space-mobile' : ''}`}>
      <div className="hotel-search-results-container">
        {!loading && <Sticker {...stickerProps} />}
        <div className="hotelwidget-wrapper Deals">{loading ? <>{isLoadingHtml}</> : <>{dealPropertyHtml}</>}</div>
      </div>
    </div>
  );
};

const PropertySearchComponent: React.FC<PropertySearchComponentProps> = inject('context')(
  observer((props) => {
    const { data, viewMoreCtaText, visibleControl, isPagination, isRandom, cTAUrl, hideOfferSummary } = props;
    const properties = data?.propertySearch;
    const onlyProperties = properties?.propertySearchListings.filter((a) => a.__typename === 'Property');

    // Filter and remove duplicates
    const loadedHotelsIds = [];
    let limitedProperties = onlyProperties?.filter((offer: any) => {
      const o = offer;
      if (checkValue(o)) {
        if (loadedHotelsIds.indexOf(o?.id) > -1) {
          return null;
        } else {
          loadedHotelsIds.push(o?.id);
          return o;
        }
      } else {
        return null;
      }
    });

    // Shuffle the array
    if (checkValue(isRandom) && isRandom?.toLocaleLowerCase() === 'true') {
      limitedProperties = checkValue(limitedProperties, true) ? shuffle(limitedProperties) : limitedProperties;
    }

    const propertySearchDateRange = properties?.searchCriteria?.dateRange;
    const [isLoadingNextPage, setIsLoadingNextPage] = React.useState<boolean>(false);
    const [propertyCount, setPropertyCount] = useState(propertiesCount);
    const isHasMoreProperties =
      checkValue(limitedProperties) &&
      limitedProperties?.length > 0 &&
      limitedProperties?.length > initialPropertiesCount &&
      Number(maxPropertiesCount) > Number(propertiesCount);
    const [hasMoreProperties, setHasMoreProperties] = useState(isHasMoreProperties);

    const dealsViewsProps: PropertySearchViewsProps = {
      properties: limitedProperties?.slice(0, propertyCount),
      allProperties: limitedProperties?.slice(0, maxPropertiesCount),
      propertySearchParam: propertySearchDateRange,
      visibleControl: visibleControl,
      isPagination: isPagination,
      hideOfferSummary: hideOfferSummary,
    };

    const loadMoreDeals = (): void => {
      setIsLoadingNextPage(true);
      propertiesCount = Number(propertyCount) + Number(initialPropertiesCount);
      setPropertyCount(propertiesCount);
      const isCheckHasMoreProperties = Number(maxPropertiesCount) > Number(propertyCount);

      if (checkValue(cTAUrl) && !isCheckHasMoreProperties) {
        let more_link = cTAUrl;
        if (!(cTAUrl.indexOf('mcicid') > -1)) {
          if (cTAUrl.indexOf('?') > -1) {
            more_link = more_link + '&mcicid=' + tracking;
          } else {
            more_link = more_link + '?mcicid=' + tracking;
          }
        }
        window.open(more_link, '_blank');
      } else {
        setHasMoreProperties(isCheckHasMoreProperties);
      }

      setIsLoadingNextPage(false);
      gaLogger.logGa('Hotel widget', 'View more button click', 'View More Hotels');
    };

    //throttle the load more deals to avoid multiple clicks at once
    const throttledLoadMoreDeals = throttle(500, loadMoreDeals);

    const loadMoreDealsMobile = (): void => {
      if (checkValue(cTAUrl)) {
        let more_link = cTAUrl;
        if (!(cTAUrl.indexOf('mcicid') > -1)) {
          if (cTAUrl.indexOf('?') > -1) {
            more_link = more_link + '&mcicid=' + tracking;
          } else {
            more_link = more_link + '?mcicid=' + tracking;
          }
        }
        window.open(more_link, '_blank');
      }
      setIsLoadingNextPage(false);
      gaLogger.logGa('Hotel widget', 'View more button click', 'View More Hotels');
    };

    const showMoreTrack = () => {
      /* */
    };

    const viewMoreCTA = (extraClasses = '', isMobile = false) => (
      <UitkPrimaryButton
        className={`show-more ga4-deals-view-more ${extraClasses}`}
        name="showMoreButton"
        type="button"
        size={UitkButtonSize.LARGE}
        onClick={checkValue(isMobile) && isMobile === true ? loadMoreDealsMobile : throttledLoadMoreDeals}
        domRef={showMoreButtonRef}
        analytics={{
          id: `${mcicidFormatText}`,
          callback: showMoreTrack,
        }}
      >
        {viewMoreCtaText}
      </UitkPrimaryButton>
    );

    const showMoreButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();
    const getVisibleControl = isGridSlickView(visibleControl);
    return (
      <>
        <PropertySearchView {...dealsViewsProps} />
        {/* MORE BUTTON VISIBLE ONLY FOR GRID-SLICK VIEW */}
        {getVisibleControl && (
          <Viewport>
            <ViewMedium>
              <>
                {hasMoreProperties && checkValue(viewMoreCtaText) && (
                  <>
                    <div className="hotel-widget-action-container eeeee">
                      <UitkLayoutFlex justifyContent="center">
                        <UitkLayoutFlexItem>{viewMoreCTA('', false)}</UitkLayoutFlexItem>
                      </UitkLayoutFlex>
                    </div>
                  </>
                )}
                {isLoadingNextPage && <DealsLoader />}
              </>
            </ViewMedium>
            <ViewSmall>
              {checkValue(cTAUrl) && checkValue(viewMoreCtaText) ? viewMoreCTA('show-more-mobile', true) : null}
            </ViewSmall>
          </Viewport>
        )}
      </>
    );
  }),
);

const PropertySearchView: React.FC<PropertySearchViewsProps> = inject('context')(
  observer((props) => {
    const { properties, allProperties, propertySearchParam, visibleControl, isPagination, hideOfferSummary } = props;
    const loadCampaign = properties;

    const options = {
      skeleton: 'dddDMMM',
      locale: props?.context?.locale || 'en',
    };
    const checkInFormatted = localization.formatDate(
      new Date(
        `${propertySearchParam?.checkInDate?.year}/${propertySearchParam?.checkInDate?.month}/${propertySearchParam?.checkInDate?.day}`,
      ),
      options,
    );
    const checkOutFormatted = localization.formatDate(
      new Date(
        `${propertySearchParam?.checkOutDate?.year}/${propertySearchParam?.checkOutDate?.month}/${propertySearchParam?.checkOutDate?.day}`,
      ),
      options,
    );
    let offerDate = `${checkInFormatted} - ${checkOutFormatted}`;
    offerDate = offerDate?.toLocaleLowerCase() === 'invalid date - invalid date' ? ' ' : offerDate;

    const renderDealsCampaignCard = (offer: any, index: number) => {
      let dealUrl = offer?.propertyDetailsLink?.uri?.relativePath;

      if (!(dealUrl.indexOf('mcicid') > -1)) {
        if (dealUrl.indexOf('?') > -1) {
          dealUrl = dealUrl + '&mcicid=' + tracking;
        } else {
          dealUrl = dealUrl + '?mcicid=' + tracking;
        }
      }

      const separatorProps: SeparatorProps = {
        modAvailabilityText: offer?.offerBadge?.secondary?.text,
        modTagText: undefined,
        discountBadge: undefined,
      };

      const guestReviewRating = `${offer?.reviews?.score}/${offer?.reviews?.scoreDenominator}`;
      const guestReviewCount = offer?.reviews?.localizedTotal;

      const hotelCardProps: HotelCardProps = {
        dealUrl: dealUrl,
        offerBadge: offer?.offerBadge?.primary?.text,
        propertyImageUrl: offer?.propertyImage?.image?.url,
        propertyImageDescription: offer?.propertyImage?.image?.description,
        name: offer?.name,
        starRating: offer?.star,
        location: offer?.neighborhood?.name,
        guestReviewRating: guestReviewRating,
        guestReviewCount: guestReviewCount,
        separatorProps: separatorProps,
        offerSummary: hideOfferSummary === 'True' ? '' : offer?.offerSummary?.messages?.[0]?.message,
        offerPrice: offer?.price,
        offerId: offer?.id,
        propertyDetailsLink: {
          referrerId: offer?.propertyDetailsLink?.clientSideAnalytics?.referrerId,
          linkName: offer?.propertyDetailsLink?.clientSideAnalytics?.linkName,
        },
      };

      return HotelCard(hotelCardProps, index, tracking, offerDate);
    };

    const dealsCarouselViewsProps: DealsCarouselViewsProps = {
      campaign: allProperties,
      renderDealsCampaignCard: renderDealsCampaignCard,
      focusElement: null,
      isPagination,
    };

    const getVisibleControl = isGridSlickView(visibleControl);

    return (
      <>
        {checkValue(loadCampaign, true) ? (
          <>
            <UitkSpacing margin={{ blockstart: 'three', inlinestart: 'three', inlineend: 'three' }}>
              {/* Grid Slick View */}
              {getVisibleControl ? (
                <>{gridSlickView(dealsCarouselViewsProps, loadCampaign, renderDealsCampaignCard)}</>
              ) : (
                <>{mediaStudioSlickView()}</>
              )}
            </UitkSpacing>
          </>
        ) : (
          <></>
        )}
      </>
    );
  }),
);

const mediaStudioSlickView = () => {
  return (
    <Viewport>
      <ViewSmall>Media Studio Slick</ViewSmall>
    </Viewport>
  );
};

export { GetHotelSearchDeals, HotelSearchComponent };
